
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import Tooltip from 'react-bootstrap/Tooltip';
import { BiCheck, BiChevronDown, BiChevronRight } from 'react-icons/bi';
import { useNavigate, useParams } from 'react-router-dom';
import { FormContext, SidebarContext } from "../../App";
import { QuestionType } from '../../core/constants/common';

export const FormSidebar = ({
	isBuildMode,
	name,
	fields,
	sections,
	currentSection,
	isSubmittedForm = false,
	submittedData,
	comments,
	onRootFieldClickCallback,
	onSectionClickCallback,
	onSubsectionClickCallback,
	onFieldDropComplete,
	onSectionDropComplete,
	validationError,
	onSidebarToggle
}) => {

	const [draggedField, setDraggedField] = useState({});
	const [draggedOver, setDraggedOver] = useState({ sectionId: 0, field: {} });
	const draggedOverRef = useRef({ sectionId: 0, field: {} });
	const [draggedSection, setDraggedSection] = useState({});
	const [answered, setAnswered] = useState({ section: {}, field: {} });
	const [commentedSections, setCommentedSections] = useState({});
	const isPreview = window.location.href.includes("form_preview");
	const { langId } = useParams();
	const { id } = useParams();
	const navigate = useNavigate();
	const [sectionVisibility, setSectionVisibility] = useState({});
	const [visibilityMapForSection, setVisibilityMapForSection] = useState({});
	const [sectionsUpdatedOn, setSectionsUpdatedOn] = useState(0);
	const { formObj } = useContext(FormContext);
	const sidebarContext = useContext(SidebarContext);
	const sidebarDisplay = sidebarContext?.sidebarDisplay ?? true;
	const setSidebarDisplay = sidebarContext?.setSidebarDisplay ?? (() => { });
	const editPermissions = formObj?.Permissions;
	const goto = (path) => {

		let url = "/form_builder/" + path + "/" + id;
		if (langId) {
			url += "/" + langId;
		}
		navigate(url);
	};

	useEffect(() => {
		updateAnsweredData(submittedData, comments);
	}, [submittedData, comments]);

	useEffect(() => {
		onSidebarToggle?.(sidebarDisplay);
	}, [sidebarDisplay, onSidebarToggle]);

	useEffect(() => {
		if (!sections || sectionsUpdatedOn > 0) return;

		const visMap = JSON.parse(JSON.stringify(visibilityMapForSection));
		setUpMapForSectionVisibility(0, sections, visMap);
		setVisibilityMapForSection(visMap);
		if (!fields || fields?.length === 0) {
			const visTrail = JSON.parse(JSON.stringify(sectionVisibility));
			setupVisibilityTrailUptoFirstSection(sections, visTrail);
			setSectionVisibility(visTrail);
		}
		setSectionsUpdatedOn(Date.now());
	}, [sections])

	const onFieldDragStart = (e, field) => {
		if (!isBuildMode) {
			return;
		}
		setDraggedField(field);
		setDraggedSection({});
	};

	const onSectionDragStart = (e, section) => {
		if (!isBuildMode) {
			return;
		}
		setDraggedSection(section);
		setDraggedField({});
	};

	const onFieldDragEnd = (e, field) => {
		if (!isBuildMode) {
			return;
		}
		const currentDraggedOver = draggedOverRef.current;
		if (draggedField.id !== currentDraggedOver.field?.id) {
			onFieldDropComplete(draggedField, currentDraggedOver.field, currentDraggedOver.sectionId);
		}
		draggedOverRef.current = { sectionId: 0, field: {} };
		setDraggedOver({ sectionId: 0, field: {} });
		setDraggedField({});
	};

	const onSectionDragEnd = (e, section) => {
		if (!isBuildMode) {
			return;
		}
		const currentDraggedOver = draggedOverRef.current;
		if (draggedSection.id !== currentDraggedOver.sectionId) {
			onSectionDropComplete(draggedSection, currentDraggedOver.field, currentDraggedOver.sectionId);
		}
		draggedOverRef.current = { sectionId: 0, field: {} };
		setDraggedOver({ sectionId: 0, field: {} });
		setDraggedSection({});
	};

	const onDragOver = (e, field, sectionId) => {
		if (!isBuildMode) {
			return;
		}
		draggedOverRef.current = { sectionId: sectionId, field: field };
		setDraggedOver({ sectionId: sectionId, field: field });
	};

	const checkValidationError = (fId) => {
		if (fId && validationError?.length > 0) {
			if (validationError.includes(fId)) {
				return true;
			}
		}
	};

	const showField = (field) => {
		if (isSubmittedForm) return false;

		return !field.hide_alt;
	};

	const canExpandOrCollapseSection = (section) => {
		if (isSubmittedForm) return section.subSections?.length > 0;

		return true;
	};

	const updateAnsweredData = (submission, reviewComments) => {
		if (isSubmittedForm) {
			let commentedData = {};
			iterateSectionsAndSetCommentedData({
				sections: sections,
				submission: submission,
				reviewComments: reviewComments,
				commentedData: commentedData
			});
			setCommentedSections(commentedData);
			return;
		}
		if (isBuildMode || isPreview || isSubmittedForm) return;

		let data = { section: {}, field: {} };
		fields?.forEach(field => {
			data.field[field.id] = isFieldAnswered(field, submission);
		});
		iterateSectionsAndSetAnsweredData({
			parentSection: null,
			sections: sections,
			submission: submission,
			data: data
		});
		setAnswered(data);
	};

	const iterateSectionsAndSetAnsweredData = ({ parentSection, sections, submission, data }) => {
		sections?.forEach(section => {
			let answeredCount = 0;
			section.fields?.forEach(field => {
				if (isFieldAnswered(field, submission)) {
					answeredCount++;
					data.field[field.id] = true;
				} else {
					data.field[field.id] = false;
				}
			});
			if (section.subSections?.length > 0) {
				iterateSectionsAndSetAnsweredData({
					parentSection: { section: section, fieldsAnswered: answeredCount === section.fields?.length },
					sections: section.subSections,
					submission: submission,
					data: data
				});
			} else {
				data.section[section.id] = answeredCount === section.fields?.length;
			}
			if (parentSection) {
				data.section[parentSection.section.id] = parentSection.fieldsAnswered && data.section[section.id];
			}
		});
	};

	const iterateSectionsAndSetCommentedData = ({ sections, submission, reviewComments, commentedData }) => {
		sections?.forEach(section => {
			section.fields?.forEach(field => {
				if (!commentedData[section.id]) {
					commentedData[section.id] = isFieldCommented(field, submission, reviewComments);
				}
			});
			if (section.subSections?.length > 0) {
				iterateSectionsAndSetCommentedData({
					sections: section.subSections,
					submission: submission,
					reviewComments: reviewComments,
					commentedData: commentedData
				});
			}
		});
	};

	const isFieldAnswered = (field, submission) => {
		let response = submission?.responses?.find(d => d.formField.id === field.id);
		if (!response) return false;

		if (field.type === QuestionType.SINGLE_TEXTBOX
			|| field.type === QuestionType.TEXT_BOX) {
			return response.textResponse !== null && response.textResponse !== '';
		}
		if (field.type === QuestionType.NUMERIC_VALUE) {
			return response.numericResponse !== null;
		}
		if (field.type === QuestionType.DROP_DOWN
			|| field.type === QuestionType.RADIO
			|| field.type === QuestionType.MULTIPLE_CHOICE
			|| field.type === QuestionType.MULTI_SELECT_DROP_DOWN) {
			return response.responseOptions?.length > 0;
		}
		if (field.type === QuestionType.DATE_TIME_BOX) {
			return response.dateResponse !== null;
		}
		if (field.type === QuestionType.DATE_RANGE) {
			return response.dateResponse !== null || response.toDateResponse !== null;
		}
		if (field.type === QuestionType.FILE_UPLOAD) {
			return response.files !== null;
		}
		if (field.type === QuestionType.TABLE) {
			return response.tableRowResponses?.length > 0;
		}
		return false;
	};

	const getSubSectionElementsToRender = (sections, sectionIndex) => {
		return (<>
			{getSortedSections(sections)?.map((section, index) => (
				<React.Fragment key={`subSecKey-${index}`}>{
					!section.hide_alt &&
					<li>
						<div className='d-flex position-relative ps-30 2'>
							{renderExpandCollapseIcon(section)}
							<a
								className={getSectionLinkClassName(section)}
								onClick={() => onSubSectionClick(section, sections)}
								draggable={true}
								onDragStart={(e) => onSectionDragStart(e, section)}
								onDragEnd={(e) => onSectionDragEnd(e, section)}>
								{
									!isBuildMode && !isPreview && answered.section[section.id] === true &&
									<BiCheck className='text-success' />
								}
								<span className={getSectionClassName(section)}>
									{`${sectionIndex}.${index + 1}. `}
									{section.title}
								</span>
							</a>
						</div>
						<ul>
							<li style={{ height: '5px' }} onDragOver={(e) => onDragOver(e, { id: 0 }, section.id)}></li>
							{
								// Spacer for the first field under a section
								isBuildMode && draggedOver.field?.id === 0 && draggedOver.sectionId === section.id && <SidebarDropSpacer />
							}
							{
								sectionVisibility[section.id] === true &&
								getSortedFields(section.fields)?.map((field, fIndex) => (
									<React.Fragment key={`subSecFKey-${index}-${fIndex}`}>{
										showField(field) &&
										<>
											<li onDragOver={(e) => onDragOver(e, field, section.id)}>
												<a className={`cursor ps-30 0 ${checkValidationError(field.id) ? 'text-danger' : ''}`}
													onClick={() => onSubsectionClickCallback(section.id, sections)}
													draggable={true}
													onDragStart={(e) => onFieldDragStart(e, field)}
													onDragEnd={(e) => onFieldDragEnd(e, field)}>
													{answered.field[field.id] === true && <BiCheck className='text-success' />}
													{field.name}
												</a>
											</li>
											{isBuildMode && field.id === draggedOver.field?.id && <SidebarDropSpacer />}
										</>
									}</React.Fragment>
								))
							}
							{getSubSectionElementsToRender(section.subSections, `${sectionIndex}.${index + 1}`)}
						</ul>
					</li>
				}</React.Fragment>
			))}
		</>);
	};

	const isFieldCommented = (field, submission, reviewComments) => {
		let response = submission?.responses?.find(d => d.formField.id === field.id);
		if (!response) return false;

		return reviewComments?.findIndex(comment => comment.responseId === response.id) >= 0;
	};

	const getSectionClassName = (section) => {
		let className = '';
		className += commentedSections[section.id] === true ? 'text-danger ' : ' ';
		return className;
	};

	const renderExpandCollapseIcon = (section) => {
		if (!canExpandOrCollapseSection(section)) return <></>;

		const isSectionExpanded = sectionVisibility[section.id] === true;
		return (
			<span className='cursor'>
				{!isSectionExpanded && <BiChevronRight onClick={() => onExpandOrCollapse(section)} className={answered.section[section.id] === true ? 'ms-0' : ''} />}
				{isSectionExpanded && <BiChevronDown onClick={() => onExpandOrCollapse(section)} className={answered.section[section.id] === true ? 'ms-0' : ''} />}
			</span>
		);
	};

	const onExpandOrCollapse = (section) => {
		const v = JSON.parse(JSON.stringify(sectionVisibility));
		v[section.id] = v[section.id] === false || v[section.id] === true ? !v[section.id] : true;
		setSectionVisibility(v);
	};

	const renderQuestionSearchBar = () => {
		const questions = [];
		fields?.forEach(f => questions.push({ ...f, rootField: true }));
		iterateAndPouplateQuestions(questions, sections, true, false);
		return (
			<div className='SearchDataCollection_box'>
				<div className='SearchDataCollection_box_inner'>
					<Typeahead
						id="quesSearchBar"
						key="quesSearchBar"
						clearButton
						selected={null}
						labelKey={(option) => option.name}
						onChange={(selected) => {
							const f = selected[0];
							if (!f) return;

							if (f.rootField) {
								onRootFieldClickCallback(f.id);
							} else if (f.sectionField) {
								onSectionClickCallback(f.sectionID);
							} else if (f.subSectionField) {
								onSubsectionClickCallback(f.sectionID, f.subSections);
							}
						}}
						options={questions ?? []}
						placeholder={"Search"} />
				</div>
			</div>

		);
	};

	const iterateAndPouplateQuestions = (questions, sectionsData, insideSection = false, insideSubSection = false) => {
		sectionsData?.forEach(s => {
			s?.fields?.forEach(f => questions.push({
				...f,
				sectionField: insideSection,
				subSectionField: insideSubSection,
				sectionID: s.id,
				subSections: sectionsData
			}));
			iterateAndPouplateQuestions(questions, s.subSections, false, true);
		});
	};

	const setUpMapForSectionVisibility = (parentSectionId, sectionsData, visMap) => {
		sectionsData?.forEach(s => {
			if (parentSectionId > 0) {
				if (!visMap[parentSectionId]) {
					visMap[parentSectionId] = [];
				}
				visMap[parentSectionId].push(s.id);
			}
			setUpMapForSectionVisibility(s.id, s.subSections, visMap);
		});
	};

	const setupVisibilityTrailUptoFirstSection = (sections, visTrail) => {
		for (let index = 0; index < sections.length; index++) {
			const s = sections[index];
			visTrail[s.id] = true;
			if (s.fields?.length > 0) {
				break;
			} else {
				setupVisibilityTrailUptoFirstSection(s.subSections, visTrail);
			}
		}
	};

	const onSectionClick = (section) => {
		const v = JSON.parse(JSON.stringify(sectionVisibility));
		v[section.id] = true;
		setSectionVisibility(v);
		onSectionClickCallback(section.id);
	};

	const onSubSectionClick = (section, sectionsData) => {
		const v = JSON.parse(JSON.stringify(sectionVisibility));
		v[section.id] = true;
		setSectionVisibility(v);
		onSubsectionClickCallback(section.id, sectionsData);
	};

	const getSectionLinkClassName = (section) => {
		let className = 'cursor';
		if (section.id === currentSection) {
			className += ' active';
		} else {
			className += ' fw-semibold';
		}
		if (validationError?.length > 0 && sectionHasValidationErrors(section)) {
			className += ' text-danger';
		};
		return className;
	};

	const sectionHasValidationErrors = (section) => {
		let hasError = false;
		for (let i = 0; i < section?.fields?.length; i++) {
			const field = section?.fields[i];
			if (field && validationError.includes(field.id)) {
				hasError = true;
				break;
			}
		}
		if (hasError) return hasError;
		for (let i = 0; i < section?.subSections?.length; i++) {
			for (let j = 0; j < section?.subSections[i]?.fields?.length; j++) {
				const field = section?.subSections[i]?.fields[j];
				if (field && validationError.includes(field.id)) {
					hasError = true;
					break;
				}
			}
			if (hasError) {
				break;
			}
		}
		return hasError;
	};

	const renderTooltip = (props) => (
		sidebarDisplay ? (
			<Tooltip id="button-tooltip" {...props}>
				Menu Hide
			</Tooltip>
		) : (
			<Tooltip id="button-tooltip" {...props}>
				Menu Show
			</Tooltip>
		)

	);

	const getSortedFields = (fields) => {
		if (!fields) return fields;
		return [...fields].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
	};

	const getSortedSections = (sections) => {
		if (!sections) return sections;
		return [...sections].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
	};

	return (
		<div id="form-sidebar" className={`FormMainBody_sidermenu 1 ${sidebarDisplay ? '' : 'FormMainBody_sidermenu_hide'}`}>
			{/* <div className='main_menu_FB'>
				<ul>
					{
						(isBuildMode || isPreview)
							?
							<>
								{editPermissions && (
									<li aria-disabled="true" onClick={() => goto('form_edit')}>
										<a className={isBuildMode ? 'active 1 cursor ' : 'Preview_menu cursor '}><MdEditNote />
											Build
										</a>
									</li>
								)}
								<li aria-disabled="true" onClick={() => goto('form_preview')}>
									<a className={isPreview ? 'active 1 cursor ' : 'Preview_menu cursor '}><BsEye />
										Preview
									</a>
								</li>
							</>
							:
							<></>

					}
				</ul>
			</div> */}

			<div className={`Create_section_menu 1 ${sidebarDisplay ? '' : 'hide'}`}>
				{renderQuestionSearchBar()}
				<ul>
					<li>
						<a className='cursor FormNameviewlist'>
							<BiChevronDown /> <h6 onClick={() => onRootFieldClickCallback(fields[0]?.id)}>{name}</h6>
						</a>
						<ul className="list-container 1">
							<li style={{ height: '5px' }} onDragOver={(e) => onDragOver(e, { id: 0 }, 0)}></li>
							{
								// Spacer for the first field under the root
								isBuildMode && draggedOver.field?.id === 0 && draggedOver.sectionId === 0 && <SidebarDropSpacer />
							}
							{getSortedFields(fields)?.map((field, index) => (
								<React.Fragment key={`fieldKey-${index}`}>{
									showField(field) &&
									<>
										<li
											onClick={() => onRootFieldClickCallback(field.id)}
											onDragOver={(e) => onDragOver(e, field, 0)}>
											<a className={`cursor ps-20 ${checkValidationError(field.id) ? 'text-danger' : ''}`}
												draggable={true}
												onDragStart={(e) => onFieldDragStart(e, field)}
												onDragEnd={(e) => onFieldDragEnd(e, field)}>
												{answered.field[field.id] === true && <BiCheck className='text-success' />}
												{field.name}
											</a>
										</li>
										{isBuildMode && field.id === draggedOver.field?.id && <SidebarDropSpacer />}
									</>
								}</React.Fragment>
							))}
							{getSortedSections(sections)?.map((section, index) => (
								<React.Fragment key={`sectionKey-${index}`}>{
									!section.hide_alt &&
									<li>
										<div className='d-flex position-relative ps-20'>
											{renderExpandCollapseIcon(section)}
											<a
												className={getSectionLinkClassName(section)}
												onClick={() => onSectionClick(section)}
												draggable={true}
												onDragStart={(e) => onSectionDragStart(e, section)}
												onDragEnd={(e) => onSectionDragEnd(e, section)} >

												{
													!isBuildMode && !isPreview && answered.section[section.id] === true &&
													<BiCheck className='text-success me-2' />
												}

												<span className={getSectionClassName(section)}>
													{`${index + 1}. `}{section.title}
												</span>
											</a>
										</div>
										<ul>
											<li style={{ height: '5px' }} onDragOver={(e) => onDragOver(e, { id: 0 }, section.id)}></li>
											{
												// Spacer for the first field under a section
												isBuildMode && draggedOver.field?.id === 0 && draggedOver.sectionId === section.id && <SidebarDropSpacer />
											}
											{
												sectionVisibility[section.id] === true &&
												getSortedFields(section.fields)?.map((sectionField, sfIndex) => (
													<React.Fragment key={`secFieldKey-${index}-${sfIndex}`}>{
														showField(sectionField) &&
														<>
															<li
																className="item-container"
																onDragOver={(e) => onDragOver(e, sectionField, section.id)}>
																<a className={`cursor ps-30 1 ${checkValidationError(sectionField.id) ? 'text-danger' : ''}`}
																	onClick={() => onSectionClickCallback(section.id)}
																	draggable={true}
																	onDragStart={(e) => onFieldDragStart(e, sectionField)}
																	onDragEnd={(e) => onFieldDragEnd(e, sectionField)}>

																	{answered.field[sectionField.id] === true && <BiCheck className='text-success' />}
																	{sectionField.name}
																</a>
															</li>
															{isBuildMode && sectionField.id === draggedOver.field?.id && <SidebarDropSpacer />}
														</>
													}</React.Fragment>
												))
											}
											{sectionVisibility[section.id] === true && getSubSectionElementsToRender(section.subSections, `${index + 1}`)}
										</ul>
									</li>
								}</React.Fragment>
							))}
						</ul>
					</li>
				</ul>
			</div>
		</div>
	);
};

export default FormSidebar;

const SidebarDropSpacer = () => (
	<li className="sidebar-drop-spacer" />
);
